import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import FilterPanel, { MovieGrid } from "./components/FilterPanel";
import { movies } from "@/mocks/movies";
import { filterMovies, getGenres, ratingOptions, yearRanges } from "@/utils/content";
import { usePageMeta } from "@/hooks/usePageMeta";

const PAGE_SIZE = 8;

export default function Category() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const keyword = searchParams.get("q") ?? "";
  const genre = searchParams.get("genre") ?? "";
  const year = searchParams.get("year") ?? "";
  const rating = searchParams.get("rating") ?? "";
  const sort = searchParams.get("sort") ?? "updated";

  const genres = useMemo(() => getGenres(), []);

  const results = useMemo(
    () => filterMovies({ keyword, genre, year, rating, sort }),
    [keyword, genre, year, rating, sort],
  );

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [keyword, genre, year, rating, sort]);

  const updateParam = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value) {
      next.set(key, value);
    } else {
      next.delete(key);
    }
    setSearchParams(next);
    setVisibleCount(PAGE_SIZE);
  };

  const resetAll = () => {
    setSearchParams(new URLSearchParams());
    setVisibleCount(PAGE_SIZE);
  };

  const titleSuffix = genre || "全部题材";
  usePageMeta({
    title: `${titleSuffix} - 日本伦理影视作品列表`,
    description: `日本伦理影视作品列表，收录 ${movies.length} 部虚构伦理题材作品。支持按题材、年代、评分区间筛选，并按更新时间、评分与年份排序，快速定位你想深入了解的影视作品。`,
    keywords: `日本伦理影视,伦理影视作品列表,${genre || "家庭伦理"},伦理影视筛选,日本伦理影视解析`,
  });

  const visibleMovies = results.slice(0, visibleCount);
  const hasMore = visibleCount < results.length;
  const activeCount = [keyword, genre, year, rating].filter(Boolean).length;

  return (
    <>
      <section className="border-b border-background-800 bg-background-900">
        <div className="mx-auto max-w-[1440px] px-4 pb-10 pt-24 md:px-12 md:pb-12 md:pt-32">
          <nav className="flex flex-wrap items-center gap-2 text-xs text-foreground-500" aria-label="面包屑">
            <Link to="/" className="transition-colors hover:text-accent-400">
              首页
            </Link>
            <i className="ri-arrow-right-s-line text-foreground-600" aria-hidden="true"></i>
            <span className="text-foreground-800">影视分类</span>
          </nav>
          <h1 className="mt-5 text-2xl font-semibold tracking-tight text-foreground-950 md:text-4xl">
            日本伦理影视作品
          </h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600">
            收录以伦理关系为核心议题的虚构影视作品，覆盖家庭伦理、社会伦理与青春伦理等方向。可按题材、年代与评分区间筛选，并结合更新时间或评分排序阅读。
          </p>
          <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-foreground-500">
            <span className="flex items-center gap-1.5">
              <i className="ri-database-2-line text-accent-500" aria-hidden="true"></i>
              共收录 {movies.length} 部作品
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-price-tag-3-line text-accent-500" aria-hidden="true"></i>
              {genres.length} 类题材
            </span>
            <span className="hidden items-center gap-1.5 sm:flex">
              <i className="ri-edit-2-line text-accent-500" aria-hidden="true"></i>
              每部作品均附剧情简介与深度解析
            </span>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[1440px] px-4 py-8 md:px-12 md:py-10">
        <FilterPanel
          keyword={keyword}
          genre={genre}
          year={year}
          rating={rating}
          sort={sort}
          resultCount={results.length}
          genres={genres}
          yearOptions={yearRanges.map((item) => ({ value: item.value, label: item.label }))}
          ratingOptions={ratingOptions.map((item) => ({ value: item.value, label: item.label }))}
          onKeywordChange={(value) => updateParam("q", value)}
          onChange={(key, value) => updateParam(key, value)}
          onReset={resetAll}
        />

        {(keyword || activeCount > 0) && (
          <div className="mt-4 flex flex-wrap items-center gap-2 text-xs text-foreground-500">
            <span>当前条件：</span>
            {keyword && (
              <button
                type="button"
                onClick={() => updateParam("q", "")}
                className="flex items-center gap-1 rounded-full bg-background-800 px-2.5 py-1 text-foreground-700"
              >
                关键词「{keyword}」
                <i className="ri-close-line" aria-hidden="true"></i>
              </button>
            )}
            {genre && (
              <button
                type="button"
                onClick={() => updateParam("genre", "")}
                className="flex items-center gap-1 rounded-full bg-background-800 px-2.5 py-1 text-foreground-700"
              >
                {genre}
                <i className="ri-close-line" aria-hidden="true"></i>
              </button>
            )}
            {year && (
              <button
                type="button"
                onClick={() => updateParam("year", "")}
                className="flex items-center gap-1 rounded-full bg-background-800 px-2.5 py-1 text-foreground-700"
              >
                {yearRanges.find((item) => item.value === year)?.label ?? year}
                <i className="ri-close-line" aria-hidden="true"></i>
              </button>
            )}
            {rating && (
              <button
                type="button"
                onClick={() => updateParam("rating", "")}
                className="flex items-center gap-1 rounded-full bg-background-800 px-2.5 py-1 text-foreground-700"
              >
                {ratingOptions.find((item) => item.value === rating)?.label ?? rating}
                <i className="ri-close-line" aria-hidden="true"></i>
              </button>
            )}
          </div>
        )}

        <div className="mt-8">
          {visibleMovies.length > 0 ? (
            <MovieGrid items={visibleMovies} />
          ) : (
            <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-700 bg-background-900/60 px-6 py-16 text-center">
              <span className="flex h-14 w-14 items-center justify-center rounded-full border border-background-700 text-foreground-500">
                <i className="ri-file-search-line text-2xl" aria-hidden="true"></i>
              </span>
              <h2 className="mt-5 text-lg font-medium text-foreground-950">
                没有符合条件的作品
              </h2>
              <p className="mt-2 max-w-md text-sm leading-relaxed text-foreground-600">
                当前筛选组合下暂无收录作品。可以尝试放宽评分区间、切换年代，或清除关键词重新检索。
              </p>
              <button
                type="button"
                onClick={resetAll}
                className="mt-6 flex items-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm text-foreground-950 transition-colors hover:bg-primary-600"
              >
                <i className="ri-refresh-line" aria-hidden="true"></i>
                <span className="whitespace-nowrap">重置全部筛选</span>
              </button>
            </div>
          )}
        </div>

        {results.length > 0 && (
          <div className="mt-10 flex flex-col items-center gap-3 border-t border-background-800 pt-8">
            <p className="text-xs text-foreground-500">
              已显示 {visibleMovies.length} / {results.length} 部作品
            </p>
            {hasMore ? (
              <button
                type="button"
                onClick={() => setVisibleCount((count) => count + PAGE_SIZE)}
                className="flex items-center gap-2 rounded-md border border-background-600 px-6 py-2.5 text-sm text-foreground-800 transition-colors hover:border-accent-500 hover:text-accent-400"
              >
                <i className="ri-add-line" aria-hidden="true"></i>
                <span className="whitespace-nowrap">加载更多作品</span>
              </button>
            ) : (
              <p className="text-xs text-foreground-500">已到达列表末尾</p>
            )}
          </div>
        )}
      </section>
    </>
  );
}